// =============================================================================
// PasteScreenshot — Orchestrator for the screenshot-to-event pipeline.
// Flow: image URL -> OCR -> score+parse -> review -> onComplete.
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle, Calendar } from 'lucide-react';
import { recognizeText } from '../services/ocrService';
import { parseScreenshotText } from '../services/screenshotParser';

// Processing states
const STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  REVIEW: 'review',
  SELECT_DATE: 'select-date',
  SELECT_TIME: 'select-time',
  ERROR: 'error',
};

export default function PasteScreenshot({ imageUrl, onComplete }) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState(null);

  // Editable fields
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [selectedDateIdx, setSelectedDateIdx] = useState(0);
  const [selectedTimeIdx, setSelectedTimeIdx] = useState(0);

  // -----------------------------------------------------------------------
  // Manual fallback
  // -----------------------------------------------------------------------
  const handleManualFallback = useCallback(() => {
    onComplete({
      title: title || '',
      date: '',
      startTime: '',
      endTime: '',
      location: location || '',
      notes: parsedData?.rawText || '',
      confidence: 'low',
    });
  }, [onComplete, title, location, parsedData]);

  // -----------------------------------------------------------------------
  // OCR + parse pipeline
  // -----------------------------------------------------------------------
  const runPipeline = useCallback(
    async (ocrResult) => {
      const parsed = parseScreenshotText(ocrResult);
      setParsedData(parsed);

      console.log('[Pipeline] Parsed result:', {
        title: parsed.title,
        date: parsed.date,
        startTime: parsed.startTime,
        endTime: parsed.endTime,
        location: parsed.location,
        confidence: parsed.confidence,
        dateCandidates: parsed.dateCandidates?.length ?? 0,
        timeCandidates: parsed.timeCandidates?.length ?? 0,
      });
      if (!parsed.title && !parsed.date && !parsed.startTime && !parsed.location) {
        console.warn('[Pipeline] No fields extracted. Raw OCR text:', parsed.rawText?.slice(0, 300));
      }

      // Populate editable fields
      setTitle(parsed.title || '');
      setDate(parsed.date || '');
      setStartTime(parsed.startTime || '');
      setEndTime(parsed.endTime || '');
      setLocation(parsed.location || '');

      // No crop step: on low confidence, go to REVIEW so user can edit.
      if (parsed.confidence === 'low') {
        setStatus(STATUS.REVIEW);
        return parsed;
      }

      // Multiple close-scoring date candidates → pick list
      if (
        parsed.dateCandidates.length > 1 &&
        parsed.dateCandidates[1].score >= parsed.dateCandidates[0].score * 0.7
      ) {
        setStatus(STATUS.SELECT_DATE);
        return parsed;
      }

      // Multiple close-scoring time candidates → pick list
      if (
        (parsed.timeCandidates || []).length > 1 &&
        parsed.timeCandidates[1].score >= parsed.timeCandidates[0].score * 0.7
      ) {
        setStatus(STATUS.SELECT_TIME);
        return parsed;
      }

      setStatus(STATUS.REVIEW);
      return parsed;
    },
    []
  );

  const processImage = useCallback(async () => {
    if (!imageUrl) return;
    setStatus(STATUS.PROCESSING);
    setError(null);

    try {
      const ocrResult = await recognizeText(imageUrl);

      if (!ocrResult.text || ocrResult.text.trim().length === 0) {
        onComplete({
          title: '',
          date: '',
          startTime: '',
          endTime: '',
          location: '',
          notes: '',
          confidence: 'low',
        });
        return;
      }

      const parsed = await runPipeline(ocrResult);

      // If nothing was extracted, retry with binarized preprocessing
      const hasData = parsed && (parsed.title || parsed.date || parsed.startTime || parsed.location);
      if (!hasData) {
        console.log('[Pipeline] No data extracted, retrying with binarized preprocessing...');
        const retryResult = await recognizeText(imageUrl, { binarize: true });
        if (retryResult.text?.trim()) {
          await runPipeline(retryResult);
        }
      }
    } catch (err) {
      console.error('Screenshot processing error:', err);
      setError(err.message || 'Failed to process screenshot');
      setStatus(STATUS.ERROR);
    }
  }, [imageUrl, onComplete, runPipeline]);

  // Auto-start on mount
  useEffect(() => {
    if (imageUrl && status === STATUS.IDLE) processImage();
  }, [imageUrl, status, processImage]);

  // -----------------------------------------------------------------------
  // Date / time candidate selection
  // -----------------------------------------------------------------------
  const handleDateSelect = (idx) => {
    setSelectedDateIdx(idx);
    const c = parsedData.dateCandidates[idx];
    setDate(c.date);
    if (c.startTime) setStartTime(c.startTime);
    if (c.endTime) setEndTime(c.endTime);
  };

  const handleConfirmDate = () => {
    if (
      (parsedData.timeCandidates || []).length > 1 &&
      parsedData.timeCandidates[1].score >= parsedData.timeCandidates[0].score * 0.7
    ) {
      setStatus(STATUS.SELECT_TIME);
    } else {
      setStatus(STATUS.REVIEW);
    }
  };

  const handleTimeSelect = (idx) => {
    setSelectedTimeIdx(idx);
    const c = parsedData.timeCandidates[idx];
    setStartTime(c.startTime);
    if (c.endTime) setEndTime(c.endTime);
  };

  const handleConfirmTime = () => setStatus(STATUS.REVIEW);

  // -----------------------------------------------------------------------
  // Submit / retry
  // -----------------------------------------------------------------------
  const handleConfirm = () => {
    onComplete({
      title,
      date,
      startTime,
      endTime,
      location,
      notes: parsedData?.rawText || '',
      confidence: parsedData?.confidence || 'medium',
    });
  };

  const handleRetry = () => {
    setStatus(STATUS.IDLE);
    setError(null);
    setParsedData(null);
    setTimeout(() => processImage(), 0);
  };

  // =======================================================================
  // Render
  // =======================================================================

  // Processing spinner
  if (status === STATUS.PROCESSING || status === STATUS.IDLE) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-4">
        <Loader2 size={40} className="text-blue-600 animate-spin" />
        <p className="text-gray-700 font-medium">Reading screenshot...</p>
        <p className="text-sm text-gray-500">Extracting text and detecting event details</p>
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Screenshot"
            className="w-32 h-auto rounded-lg border border-gray-200 mt-4 opacity-60"
          />
        )}
      </div>
    );
  }

  // Error
  if (status === STATUS.ERROR) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-4">
        <AlertTriangle size={40} className="text-amber-500" />
        <p className="text-gray-900 font-semibold">Couldn't read screenshot</p>
        <p className="text-sm text-gray-600 text-center">{error}</p>
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={handleManualFallback}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Enter Manually
          </button>
        </div>
      </div>
    );
  }

  // Date candidate selection
  if (status === STATUS.SELECT_DATE && parsedData) {
    return (
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="text-center mb-2">
          <Calendar size={24} className="text-blue-600 mx-auto mb-2" />
          <p className="text-gray-900 font-semibold">Multiple dates detected</p>
          <p className="text-sm text-gray-600">Which date is this event for?</p>
        </div>
        <div className="space-y-2">
          {parsedData.dateCandidates.map((candidate, idx) => (
            <button
              key={candidate.date}
              onClick={() => handleDateSelect(idx)}
              className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                selectedDateIdx === idx
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <span className="font-medium text-gray-900">{candidate.label}</span>
              <span className="text-sm text-gray-500 ml-2">({candidate.date})</span>
            </button>
          ))}
        </div>
        <button
          onClick={handleConfirmDate}
          className="w-full mt-4 bg-blue-600 text-white rounded-lg p-3 font-medium hover:bg-blue-700 transition-colors"
        >
          Continue with Selected Date
        </button>
      </div>
    );
  }

  // Time candidate selection
  if (status === STATUS.SELECT_TIME && parsedData?.timeCandidates) {
    return (
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="text-center mb-2">
          <Calendar size={24} className="text-blue-600 mx-auto mb-2" />
          <p className="text-gray-900 font-semibold">Multiple times detected</p>
          <p className="text-sm text-gray-600">Which time is correct?</p>
        </div>
        <div className="space-y-2">
          {parsedData.timeCandidates.map((candidate, idx) => {
            const label = candidate.endTime
              ? `${candidate.startTime} – ${candidate.endTime}`
              : candidate.startTime;
            return (
              <button
                key={`${candidate.startTime}-${candidate.endTime || ''}`}
                onClick={() => handleTimeSelect(idx)}
                className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                  selectedTimeIdx === idx
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <span className="font-medium text-gray-900">{label}</span>
                <span className="text-sm text-gray-500 ml-2">({candidate.label})</span>
              </button>
            );
          })}
        </div>
        <button
          onClick={handleConfirmTime}
          className="w-full mt-4 bg-blue-600 text-white rounded-lg p-3 font-medium hover:bg-blue-700 transition-colors"
        >
          Continue with Selected Time
        </button>
      </div>
    );
  }

  // Review screen
  if (status === STATUS.REVIEW) {
    return (
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {imageUrl && (
          <div className="flex justify-center">
            <img
              src={imageUrl}
              alt="Screenshot"
              className="w-24 h-auto rounded-lg border border-gray-200"
            />
          </div>
        )}

        {(title || date || startTime || endTime || location) ? (
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className="text-green-600" />
            <span className="text-sm text-green-700 font-medium">Event details detected</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <span className="text-sm text-amber-600 font-medium">
              No event details detected — enter manually or try a different screenshot
            </span>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event title"
            className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Start Time</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">End Time</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Add location"
            className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {parsedData?.timezone && parsedData.timezone !== 'America/New_York' && (
          <div className="text-sm text-gray-500">Timezone: {parsedData.timezone}</div>
        )}

        {parsedData?.rawText && (
          <details className="text-sm border border-gray-200 rounded-lg">
            <summary className="px-3 py-2 text-gray-500 cursor-pointer hover:text-gray-700">
              Show raw text from screenshot
            </summary>
            <pre className="px-3 py-2 text-xs text-gray-600 whitespace-pre-wrap max-h-40 overflow-auto border-t border-gray-200 bg-gray-50">
              {parsedData.rawText}
            </pre>
          </details>
        )}

        <div className="space-y-2 pt-2">
          <button
            onClick={handleConfirm}
            className="w-full bg-blue-600 text-white rounded-lg p-3 font-medium hover:bg-blue-700 transition-colors"
          >
            Create Event
          </button>
          <button
            onClick={handleManualFallback}
            className="w-full bg-gray-100 text-gray-700 rounded-lg p-3 font-medium hover:bg-gray-200 transition-colors text-sm"
          >
            Edit More Details
          </button>
        </div>
      </div>
    );
  }

  return null;
}